// Канонический setError — глобальный mixin (main.js: Vue.mixin), замена 108
// копипастных копий в компонентах. Поведение — супермножество вариантов:
// полные гварды на разлогин (Signin), сброс зависшего isLoading (PageTemplate,
// Signin), this.error для страниц, что его рендерят (лишним не бывает).
// Транзиентные сетевые ошибки НЕ показывают красный тост — их индицирует
// глобальный баннер «Проблема с сетью» в App.vue (см. createNetworkFlagInterceptor).
import { isTransientNetworkError, isLongOperationTimeout } from "@/utils/networkErrors";
import { isSessionDeadError } from "@/utils/sessionExpiry";
import { isMoneyDeclined } from "@/utils/moneyConfirmGuard";

// Ожидаемый 422 (валидация) — не баг: в Rollbar не шлём (шум, тот же класс
// гигиены, что network-noise), пользователю показываем ПРИЧИНУ сервера, а
// не генерик. Инцидент 2026-07-22: «Ошибка при добавлении бронирования!»
// скрывала «у машины нет почасовой цены» (no_price) — пользователь не
// понимал, что чинить, а Rollbar копил не-баги. Модульная функция (не
// метод): setError зовут и с фейковыми this в спеках компонентов.
// Легаси-строки бизнес-отказов сервера (406 not_acceptable) → i18n-ключ.
// Issue #127: «Ошибка удаления архивных машин» скрывала «Can't destroy,
// has bookings» — осознанный отказ, а не сбой.
const BUSINESS_REFUSAL_KEYS = {
  "Can't destroy, has bookings": "car_has_bookings",
  "Can't deactivate, has active bookings": "car_has_active_bookings",
  "Cars limit reached": "cars_limit_reached",
  "Trial period ended": "trial_ended",
  "periods in branches must be equal": "periods_must_be_equal",
};

// 404 обрабатывается ИЗБИРАТЕЛЬНО, в отличие от 422/406.
//
// Целиком «ожидаемым» его делать нельзя: 404 на несуществующем маршруте или на
// сущности, которая обязана была быть, — настоящий баг, и он должен и дальше
// попадать в Rollbar красным. Поэтому ожидаемым считается только 404, у
// которого сервер вернул УЗНАННУЮ строку отказа. Всё остальное падает в общую
// ветку без изменений.
//
// Причина появления (2026-08-04): машину переносят в другой филиал
// (PATCH /api/v1/cars/:id со сменой company_id), а у сотрудника старого
// филиала остаётся открытая карточка. Вкладка «Обслуживание» дёргает
// /api/v1/car_maintenances/:car_id, тот честно отвечает 404 — но сотрудник
// видел общий «Ошибка», а Rollbar собирал ~230 таких не-багов в сутки.
const NOT_FOUND_REFUSAL_KEYS = {
  "Car not found in your branch": "car_in_other_branch",
};

export function extractValidationDetail(error) {
  const data = error && error.response && error.response.data;
  if (!data) return { code: null, detail: null };
  const err = data.error;
  if (err && typeof err === "object") {
    return { code: err.code || null, detail: err.message || null };
  }
  if (typeof err === "string") return { code: null, detail: err };
  if (typeof data === "object") {
    // render json: booking.errors — hash {поле: ["сообщение", ...]}
    const msgs = Object.values(data).flat().filter((v) => typeof v === "string");
    if (msgs.length) return { code: null, detail: msgs.join("; ") };
  }
  return { code: null, detail: null };
}

export default {
  methods: {
    // options.silent — «человеку уже сказано»: отказ объясняет блок на самой
    // странице (LoadFailedRetry), и красный тост поверх него был бы вторым
    // сообщением об одной ошибке. Репорт в Rollbar при этом остаётся.
    setError(error, text, options) {
      const silent = !!(options && options.silent);
      // FT-136: 401 класса «сессия мертва» уже объяснён источником — интерсептор
      // показал единственный тост и оставил нотис форме входа ДО перехода. Второй
      // тост не добавится, а затрёт первый: снекбар в приложении один
      // (notifications.js:63-67 + App.vue:248-256). Rollbar тоже не зовём: истёкший
      // JWT — ожидаемое состояние, как 422 и 406 ниже, а не баг.
      if (isSessionDeadError(error)) {
        if (this.isLoading === true) {
          this.isLoading = false;
        }
        return;
      }

      // Оператор сам отказался отправлять необычно крупную сумму (moneyConfirmGuard).
      // Запрос отменён ДО отправки, сбоя не было. Тот же класс, что и ветка выше:
      // тост тут показал бы «ошибку» на осознанное действие человека, а Rollbar
      // копил бы не-баги. Единственное, что нужно, — снять зависший isLoading:
      // форма ушла в отправку и без этого осталась бы заблокированной.
      if (isMoneyDeclined(error)) {
        if (this.isLoading === true) {
          this.isLoading = false;
        }
        return;
      }

      const status = error && error.response && error.response.status;
      const { code, detail } = extractValidationDetail(error);
      // 422 = валидация, 406 = бизнес-отказ (легаси cars-контроллер) — оба
      // ожидаемые целиком: warning с причиной, Rollbar не зовём.
      // 404 — только с узнанной строкой отказа (см. NOT_FOUND_REFUSAL_KEYS).
      const notFoundSlug = status === 404 && detail ? NOT_FOUND_REFUSAL_KEYS[detail] : null;
      // Считается один раз на оба пути: сервер шлёт бизнес-отказы как
      // { error: { code, message } } с английским message, и переводить их по
      // коду надо независимо от того, ожидаемый это статус или нет. Иначе на
      // 400/402/404/429 пользователь увидит английский текст сервера.
      const codeKey = code && this.$te(`errors.codes.${code}`) ? `errors.codes.${code}` : null;
      if (status === 422 || status === 406 || notFoundSlug) {
        const refusalSlug = notFoundSlug || (detail && BUSINESS_REFUSAL_KEYS[detail]);
        const refusalKey = refusalSlug && this.$te(`errors.refusals.${refusalSlug}`) ? `errors.refusals.${refusalSlug}` : null;
        const human = (codeKey && this.$t(codeKey)) || (refusalKey && this.$t(refusalKey)) || detail || text;
        if (!silent) this.$notify(text, human, "warning", 6000);
        console.log(error);
        if (this.isLoading === true) {
          this.isLoading = false;
        }
        this.error = human;
        return;
      }

      const user = this.$store ? this.$store.getters.getCurrentUser : null;
      const companyWrap = this.$store ? this.$store.getters.getCompany : null;
      this.$rollbar.configure({
        payload: {
          person: {
            id: user ? user.id : null,
            username: user && user.name ? user.name : null,
            email: user && user.email ? user.email : null,
          },
          custom: {
            company:
              companyWrap && companyWrap.company && companyWrap.company.name
                ? companyWrap.company.name
                : null,
          },
        },
      });
      this.$reportError(text, error);
      if (isLongOperationTimeout(error)) {
        // Долгая операция не уложилась в СВОЙ поднятый таймаут. Глобальный
        // баннер «Проблема с сетью» этот случай намеренно пропускает
        // (networkErrors.js), поэтому объяснить обязано это место — иначе
        // экран промолчал бы вовсе. Сырой `${error}` тут не нужен: строка
        // «timeout of 75000ms exceeded» пользователю ничего не говорит.
        if (!silent) this.$notify(text, this.$t("errors.operation_timeout"), "warning", 6000);
      } else if (!isTransientNetworkError(error) && !silent) {
        this.$notify(this.$t("errors.error"), `${error}. ${text}`, "error", 5000);
      }
      console.log(error);
      if (this.isLoading === true) {
        this.isLoading = false;
      }
      // detail — уже нормализованная строка (extractValidationDetail достаёт
      // message из объектной формы). Раньше здесь лежал сырой data.error, и
      // на { error: { code, message } } в this.error попадал ОБЪЕКТ — шаблоны,
      // которые его рендерят, показывали "[object Object]".
      this.error = (codeKey && this.$t(codeKey)) || detail || text;
    },
  },
};
